import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Tag } from "@/components/common/badges";
import { Field, Panel } from "@/components/common/page";
import { Stat } from "@/components/common/stat";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { q } from "@/data/queries";
import { formatDateTime, formatNumber, formatUsd } from "@/lib/format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/p/$project/settings/billing")({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(q.billingPeriod()),
      context.queryClient.ensureQueryData(q.invoices()),
      context.queryClient.ensureQueryData(q.accounts({ limit: 200 })),
    ]),
  component: BillingPage,
});

function BillingPage() {
  const { data: billing } = useSuspenseQuery(q.billingPeriod());
  const { data: invoices } = useSuspenseQuery(q.invoices());
  const { data: accounts } = useSuspenseQuery(q.accounts({ limit: 200 }));
  const over = Math.max(0, billing.accountsUsed - billing.accountsIncluded);
  const overageCents = over * billing.accountOverageCents;
  const baseCents = billing.projectedCents - overageCents;

  return (
    <>
      <Panel
        title={`${billing.planName} · ${formatDateTime(billing.startsAt).slice(0, 6)} to ${formatDateTime(billing.endsAt).slice(0, 6)}`}
        actions={
          <Button variant="outline" size="sm" disabled>
            Change plan
          </Button>
        }
        bodyClassName="p-0"
      >
        <div className="grid grid-cols-2 gap-px bg-border lg:grid-cols-3">
          <Stat
            label="Projected this period"
            value={formatUsd(billing.projectedCents / 100, false)}
            footnote={
              over > 0
                ? `${formatUsd(baseCents / 100, false)} plan + ${formatUsd(overageCents / 100, false)} accounts`
                : "No overage"
            }
            className="bg-card"
          />
          <Stat
            label="Connected accounts"
            value={billing.accountsUsed}
            unit={`/ ${billing.accountsIncluded}`}
            tone={over > 0 ? "warn" : undefined}
            footnote={
              over > 0
                ? `${over} over, at ${formatUsd(billing.accountOverageCents / 100, false)} each`
                : undefined
            }
            className="bg-card"
          />
          <Stat
            label="API calls"
            value={formatNumber(billing.callsUsed)}
            unit={`/ ${formatNumber(billing.callsIncluded)}`}
            className="bg-card"
          />
        </div>
      </Panel>

      <Panel title="This period" bodyClassName="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-[12.5px]">
            <span className="text-muted-foreground">
              Connected accounts — the unit the plan meters on
            </span>
            <span className="tabular">
              {billing.accountsUsed} of {billing.accountsIncluded} included
            </span>
          </div>
          <Progress
            value={Math.min(
              100,
              (billing.accountsUsed / billing.accountsIncluded) * 100,
            )}
            className={cn("h-1.5", over > 0 && "[&>div]:bg-warn")}
          />
          <p className="text-[11.5px] text-muted-foreground">
            One account is one mailbox, calendar or address book a user has linked. A
            paused account still counts; a disconnected one does not.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-[12.5px]">
            <span className="text-muted-foreground">API calls</span>
            <span className="tabular">
              {formatNumber(billing.callsUsed)} of {formatNumber(billing.callsIncluded)}
            </span>
          </div>
          <Progress
            value={(billing.callsUsed / billing.callsIncluded) * 100}
            className="h-1.5"
          />
        </div>
      </Panel>

      <Panel
        title="What counts"
        description="So the number on the invoice is never a surprise"
        bodyClassName="px-4 py-2"
      >
        <dl>
          <Field label="Counted">
            {accounts.items.filter((a) => a.status !== "disconnected").length} accounts
            in connected, syncing, needs-attention or paused
          </Field>
          <Field label="Not counted">
            {accounts.items.filter((a) => a.status === "disconnected").length}{" "}
            disconnected, and everything in sandbox
          </Field>
          <Field label="Not metered">
            Webhook deliveries, retries and replays. Receiving an event is not a call
            you made.
          </Field>
          <Field label="Per message">
            Nothing. Aux0 charges per connected account, not per message sent or event
            created.
          </Field>
        </dl>
      </Panel>

      <Panel title="Invoices" bodyClassName="divide-y">
        {invoices.map((invoice) => (
          <div key={invoice.id} className="flex items-center gap-3 px-4 py-2.5">
            <span className="min-w-0 flex-1">
              <span className="block font-mono text-[12.5px]">{invoice.number}</span>
              <span className="block text-[11.5px] text-muted-foreground">
                {formatDateTime(invoice.periodStart).slice(0, 6)} to{" "}
                {formatDateTime(invoice.periodEnd).slice(0, 6)}
              </span>
            </span>
            <Tag
              className={
                invoice.status === "paid"
                  ? "border-ok/30 bg-ok-soft/60 text-ok"
                  : "border-border text-muted-foreground"
              }
            >
              {invoice.status}
            </Tag>
            <span className="w-20 shrink-0 text-right text-[13px] tabular">
              {formatUsd(invoice.amountCents / 100, false)}
            </span>
          </div>
        ))}
      </Panel>
    </>
  );
}
